import { useParams } from 'react-router';
import { ReactNode } from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { RubricBankConstruct } from 'libs/end-to-end/src/types/rubric-bank/rubric-bank-construct';
import { RubricBankConstructForWrite } from 'libs/end-to-end/src/types/rubric-bank/rubric-bank-construct';
import { RubricBankConstructItem } from 'libs/end-to-end/src/types/rubric-bank/rubric-bank-construct';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import React from 'react';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { useRubricItemsData } from 'apps/sigma2-ui/src/data-wizard/components/use-rubric-items-data';
import { Modal } from 'apps/sigma2-ui/src/components/ui/Modal';
import { DatabaseRubricBankItemsView } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseRubricBankItemsView';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';

export const DatabaseRubricBankConstructPage = () => {
  const { organizationId, databaseId, constructId } = useParams();

  const navigate = useNavigateSafe();

  if (!organizationId || !databaseId || !constructId) {
    throw new Error(
      'DatabaseRubricBankPage: organizationId or databaseId or constructId is not defined',
    );
  }

  const [construct, setConstruct] = useState<RubricBankConstruct | null>(null);

  const getUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/rubric-bank-constructs/${constructId}`;
  }, [organizationId, databaseId, constructId]);

  const readConstructsPattern = useSimpleReadPattern({
    getUrlFn,
    canRead: true,
    responseSchema: RubricBankConstruct,
  });

  const updateConstructPattern = useSimpleWritePattern({
    getUrlFn,
    method: 'PUT',
    canWrite: true,
    writeSchema: RubricBankConstructForWrite,
    responseSchema: EmptyObject,
  });

  const setConstructItems = useCallback(
    (items: RubricBankConstructItem[]) => {
      if (construct) {
        setConstruct({
          ...construct,
          items,
        });
      }
    },
    [construct, setConstruct],
  );

  const constructItemsOperations = useCrudOperations({
    items: construct ? construct.items : [],
    setItems: setConstructItems,
    postProcessNewItems: (newItems) => {
      return newItems.map((item) => {
        return {
          ...item,
          id: item.id,
        };
      });
    },
  });

  useEffect(() => {
    if (readConstructsPattern.status === 'done') {
      setConstruct(readConstructsPattern.entity);
    }
  }, [readConstructsPattern]);

  const isLoading = useMemo(() => {
    return (
      readConstructsPattern.status === 'loading' ||
      updateConstructPattern.status === 'saving'
    );
  }, [readConstructsPattern, updateConstructPattern]);

  const [showItemsModal, setShowItemsModal] = useState(false);
  const [currentConstructItemIndex, setCurrentConstructItemIndex] = useState<
    number | null
  >(null);

  const rubricItemsDataSingleSelectConfig = useMemo(() => {
    if (currentConstructItemIndex === null) {
      return null;
    }

    const itemId = construct?.items.at(currentConstructItemIndex)?.rubricId;

    if (!itemId) {
      return {
        initialSelectedItemId: null,
      };
    }

    return {
      initialSelectedItemId: itemId,
    };
  }, [construct, currentConstructItemIndex]);

  const useRubricItemsDataPayload = useRubricItemsData({
    organizationId,
    databaseId,
    singleSelectConfig: rubricItemsDataSingleSelectConfig,
  });

  const deleteConstructPatternGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/rubric-bank-constructs/${constructId}`;
  }, [organizationId, databaseId, constructId]);

  const deleteConstructPattern = useSimpleWritePattern({
    getUrlFn: deleteConstructPatternGetUrlFn,
    method: 'DELETE',
    canWrite: true,
    writeSchema: EmptyObject,
    responseSchema: EmptyObject,
  });

  const onDelete = useCallback(() => {
    if (deleteConstructPattern.status !== 'saving') {
      deleteConstructPattern.saveEntity({}, () => {
        navigate(
          `/organizations/${organizationId}/databases/${databaseId}/rubric-bank`,
        );
      });
    }
  }, [deleteConstructPattern, navigate, organizationId, databaseId]);

  return (
    <AdminLayout currentPage={'DatabaseRubricBankConstruct'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Databases',
            to: `/organizations/${organizationId}/databases`,
          },
          {
            label: 'Edit Database',
            to: `/organizations/${organizationId}/databases/edit/${databaseId}`,
          },
          {
            label: 'Rubric Bank',
            to: `/organizations/${organizationId}/databases/${databaseId}/rubric-bank`,
          },
          {
            label: 'Rubric Bank Construct',
            to: `/organizations/${organizationId}/databases/${databaseId}/rubric-bank-constructs/${constructId}`,
          },
        ]}
      />
      <Modal
        show={showItemsModal}
        onClose={() => {
          if (
            construct &&
            currentConstructItemIndex !== null &&
            useRubricItemsDataPayload.singleSelectConfig?.currentItemId
          ) {
            constructItemsOperations.onEdit(currentConstructItemIndex, {
              rubricId:
                useRubricItemsDataPayload.singleSelectConfig.currentItemId,
            });
          }
          setShowItemsModal(false);
        }}
        title={'Choose Rubric Items'}
        maxWidthOverride="max-w-screen-md"
        scrollable={true}
      >
        <DatabaseRubricBankItemsView
          payload={useRubricItemsDataPayload}
          isEditable={false}
        />
      </Modal>
      <Spacing size={'md'} direction={'vertical'}>
        {construct && (
          <Spacing size={'md'} direction={'vertical'}>
            <input
              className="form-input"
              type={'text'}
              value={construct.name}
              onChange={(e) => {
                if (construct) {
                  setConstruct({
                    ...construct,
                    name: e.target.value,
                  });
                }
              }}
            />

            <Button
              behavior={{
                type: 'onClick',
                onClick: () => {
                  constructItemsOperations.onAdd({
                    id: '2',
                    key: '',
                    rubricId: '',
                  });
                },
              }}
              size={'small'}
              text={'Add Question'}
            />

            <Table
              columnDefinitions={[
                {
                  id: 'id',
                  header: () => 'Id',
                  cell: ({ item }) => <div>{item.id}</div>,
                },
                {
                  id: 'key',
                  header: () => 'Key',
                  cell: ({ item, rowIndex }) => (
                    <input
                      className="form-input"
                      type={'text'}
                      value={item.key}
                      onChange={(e) => {
                        if (construct) {
                          constructItemsOperations.onEdit(rowIndex, {
                            ...item,
                            key: e.target.value,
                          });
                        }
                      }}
                    />
                  ),
                },
                {
                  id: 'rubric_item',
                  header: () => 'Rubric Item',
                  cell: ({ item, rowIndex }) => {
                    const foundItem = useRubricItemsDataPayload.items.find(
                      (i) => i.id === item.rubricId,
                    );

                    let itemIdItem: ReactNode;

                    if (item.rubricId === '' || !foundItem) {
                      itemIdItem = <div>{'N/A'}</div>;
                    } else {
                      itemIdItem = <div>{foundItem.name}</div>;
                    }

                    return (
                      <Spacing size={'md'} direction={'vertical'}>
                        {itemIdItem}
                        <Button
                          behavior={{
                            type: 'onClick',
                            onClick: () => {
                              setCurrentConstructItemIndex(rowIndex);
                              setShowItemsModal(true);
                            },
                          }}
                          size={'small'}
                          variant={'outline'}
                          text={'Select Item'}
                        />
                      </Spacing>
                    );
                  },
                },
                {
                  id: 'delete',
                  header: () => <div>Delete</div>,
                  cell: ({ rowIndex }) => (
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: () => {
                          constructItemsOperations.onDelete(rowIndex);
                        },
                      }}
                      size={'small'}
                      variant={'outline'}
                      text={'Delete'}
                    />
                  ),
                },
              ]}
              items={construct.items}
              isLoading={isLoading}
            />
          </Spacing>
        )}
        <Spacing size={'justify-between'} direction={'horizontal'}>
          <Button
            behavior={{
              type: 'onClick',
              onClick: () => {
                if (updateConstructPattern.status !== 'saving' && construct) {
                  updateConstructPattern.saveEntity(construct);
                }
              },
            }}
            variant={'primary'}
            size={'small'}
            text={'Save'}
          />
          <Button
            behavior={{
              type: 'onClick',
              onClick: () => {
                onDelete();
              },
            }}
            size={'small'}
            disabled={isLoading}
            variant={'destructive'}
            text={'Delete Construct'}
          />
        </Spacing>
      </Spacing>
    </AdminLayout>
  );
};
